import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from './UserManagement';
import { HINDI_PHONEMES, HINDI_PHONEME_CATEGORIES } from '../data/hindiPhonemes';

interface Sentence {
  id: string;
  language: string;
  text: string;
  transliteration: string;
  translation_en: string;
  status: string;
  difficulty: string;
  category: string;
  reference_audio_url?: string;
  pending_audio_url?: string;
  coach_id?: string;
  admin_id?: string;
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
}

interface PhonemeRecording {
  id: string;
  language: string;
  accent: string;
  phoneme: string;
  coachId: string;
  status: string;
  audioUrl: string;
  publicUrl?: string;
  createdAt: any;
  updatedAt?: any;
}

interface PerformanceLog {
  id: string;
  duration: number;
  analysisMode: string;
  timestamp: string;
  hasError: boolean;
  errorMessage?: string;
}

const AdminDashboardEnhanced: React.FC = () => {
  console.log('AdminDashboardEnhanced v2 - with phoneme support');
  const { getIdToken } = useAuth();
  const [activeTab, setActiveTab] = useState<'performance' | 'audio-approval' | 'sentence-management' | 'user-management' | 'game-settings'>('audio-approval');
  const [pendingSentences, setPendingSentences] = useState<Sentence[]>([]);
  const [pendingPhonemes, setPendingPhonemes] = useState<PhonemeRecording[]>([]);
  const [allSentences, setAllSentences] = useState<Sentence[]>([]);
  const [performanceLogs, setPerformanceLogs] = useState<PerformanceLog[]>([]);
  const [selectedSentence, setSelectedSentence] = useState<Sentence | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [adminId] = useState('admin_demo_001'); // In production, get from auth
  const [showAddSentenceModal, setShowAddSentenceModal] = useState(false);
  const [newSentence, setNewSentence] = useState({
    language: 'hi-IN',
    text: '',
    transliteration: '',
    translation_en: '',
    difficulty: 'beginner',
    category: 'general'
  });
  const [beginnerPhonemes, setBeginnerPhonemes] = useState<Set<string>>(new Set([
    // Default beginner-friendly phonemes
    'ə', 'aː', 'k', 'g', 'p', 'b', 'm', 'n', 'l', 's', 'h'
  ]));
  
  // Determine backend URL based on environment
  const backendUrl = window.location.hostname === 'localhost' 
    ? 'http://localhost:8080'
    : 'https://voice-coach-backend-406125116283.us-east1.run.app';
  
  // Helper to get auth headers
  const getAuthHeaders = async () => {
    try {
      const token = await getIdToken();
      console.log('Got token:', token ? `${token.substring(0, 20)}...` : 'null');
      if (!token) {
        console.error('No auth token available - user may not be signed in');
        throw new Error('Not authenticated');
      }
      return {
        'Authorization': `Bearer ${token}`
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (activeTab === 'audio-approval') {
      fetchPendingSentences();
      fetchPendingPhonemes();
    } else if (activeTab === 'sentence-management') {
      fetchAllSentences();
    } else if (activeTab === 'performance') {
      fetchPerformanceLogs();
    } else if (activeTab === 'user-management') {
      // UserManagement component handles its own data fetching
    }
  }, [activeTab]);

  const fetchPendingSentences = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(
        `${backendUrl}/api/sentences?status=pending_approval`,
        {
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch pending sentences');
      }

      const data = await response.json();
      setPendingSentences(data.sentences || []);
    } catch (err) {
      console.error('Error fetching pending sentences:', err);
      setError('Failed to load pending sentences');
      // Use mock data for demo
      setPendingSentences([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingPhonemes = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${backendUrl}/api/phonemes/pending`,
        {
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch pending phoneme recordings');
      }

      const data = await response.json();
      setPendingPhonemes(data || []);
    } catch (err) {
      console.error('Error fetching pending phoneme recordings:', err);
      setPendingPhonemes([]);
    }
  };

  const fetchAllSentences = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/sentences?limit=100`,
        {
          headers: {
            ...(await getAuthHeaders())
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch sentences');
      }

      const data = await response.json();
      setAllSentences(data.sentences || []);
    } catch (err) {
      console.error('Error fetching sentences:', err);
      setError('Failed to load sentences');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPerformanceLogs = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/performance-stats?days=7`
      );
      
      if (response.ok) {
        const data = await response.json();
        setPerformanceLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Error fetching performance logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const playAudio = (audioUrl: string) => {
    const audio = new Audio(audioUrl);
    audio.play().catch(err => {
      console.error('Failed to play audio:', err);
      setError('Failed to play audio');
    });
  };

  const approvePhonemeRecording = async (phoneme: PhonemeRecording) => {
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/phonemes/${phoneme.id}/approve`,
        {
          method: 'POST',
          headers: {
            ...(await getAuthHeaders())
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve phoneme recording');
      }

      setSuccessMessage(`Phoneme recording approved: ${phoneme.phoneme}`);
      fetchPendingPhonemes(); // Refresh list
    } catch (err) {
      console.error('Error approving phoneme recording:', err);
      setError('Failed to approve phoneme recording');
    }
  };

  const rejectPhonemeRecording = async (phoneme: PhonemeRecording) => {
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/phonemes/${phoneme.id}/reject`,
        {
          method: 'POST',
          headers: {
            ...(await getAuthHeaders())
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reject phoneme recording');
      }

      setSuccessMessage(`Phoneme recording rejected: ${phoneme.phoneme}`);
      fetchPendingPhonemes(); // Refresh list
    } catch (err) {
      console.error('Error rejecting phoneme recording:', err);
      setError('Failed to reject phoneme recording');
    }
  };

  const approveRecording = async (sentence: Sentence) => {
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/sentences/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
          },
          body: JSON.stringify({
            sentence_id: sentence.id,
            admin_id: adminId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve recording');
      }

      setSuccessMessage(`Recording approved for: ${sentence.text}`);
      fetchPendingSentences(); // Refresh list
      setSelectedSentence(null);
    } catch (err) {
      console.error('Error approving recording:', err);
      setError('Failed to approve recording');
    }
  };

  const rejectRecording = async () => {
    if (!selectedSentence) return;
    
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/sentences/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
          },
          body: JSON.stringify({
            sentence_id: selectedSentence.id,
            admin_id: adminId,
            rejection_reason: rejectionReason
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reject recording');
      }

      setSuccessMessage(`Recording rejected for: ${selectedSentence.text}`);
      setShowRejectionModal(false);
      setRejectionReason('');
      fetchPendingSentences(); // Refresh list
      setSelectedSentence(null);
    } catch (err) {
      console.error('Error rejecting recording:', err);
      setError('Failed to reject recording');
    }
  };

  const addNewSentence = async () => {
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/sentences`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
          },
          body: JSON.stringify(newSentence)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add sentence');
      }

      setSuccessMessage('Sentence added successfully');
      setShowAddSentenceModal(false);
      setNewSentence({
        language: 'hi-IN',
        text: '',
        transliteration: '',
        translation_en: '',
        difficulty: 'beginner',
        category: 'general'
      });
      fetchAllSentences(); // Refresh list
    } catch (err) {
      console.error('Error adding sentence:', err);
      setError('Failed to add sentence');
    }
  };

  const importSentencesFromJSON = async (language: 'hindi' | 'english' = 'hindi') => {
    try {
      // Fetch local JSON based on language
      const jsonFile = language === 'hindi' ? '/hindi-sentences.json' : '/english-sentences.json';
      const response = await fetch(jsonFile);
      const localSentences = await response.json();
      
      // Transform sentences to match backend format
      const transformedSentences = localSentences.map((s: any) => ({
        ...s,
        language: language === 'hindi' ? 'hi-IN' : 'en-US',
        translation_en: s.english || s.text || s.translation_en,
        text: s.hindi || s.text
      }));
      
      // Import to backend
      const importResponse = await fetch(
        `${backendUrl}/api/sentences/bulk-import`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
          },
          body: JSON.stringify({ sentences: transformedSentences })
        }
      );

      if (!importResponse.ok) {
        const errorData = await importResponse.text();
        console.error('Import failed:', errorData);
        throw new Error('Failed to import sentences');
      }

      const result = await importResponse.json();
      setSuccessMessage(result.message);
      fetchAllSentences();
    } catch (err) {
      console.error('Error importing sentences:', err);
      setError(`Failed to import ${language} sentences`);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; text: string }> = {
      draft: { color: '#6c757d', text: 'Draft' },
      pending_approval: { color: '#ffc107', text: 'Pending' },
      approved: { color: '#28a745', text: 'Approved' },
      rejected: { color: '#dc3545', text: 'Rejected' }
    };
    
    const badge = badges[status] || badges.draft;
    
    return (
      <span 
        style={{
          backgroundColor: badge.color,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '0.85rem',
          fontWeight: 600
        }}
      >
        {badge.text}
      </span>
    );
  };

  return (
    <div className="admin-dashboard enhanced">
      <div className="dashboard-header">
        <h2>⚙️ Admin Dashboard</h2>
        <p>Manage content, approve recordings, and monitor performance</p>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab-btn ${activeTab === 'audio-approval' ? 'active' : ''}`}
          onClick={() => setActiveTab('audio-approval')}
        >
          🎵 Audio Approval ({pendingSentences.length + pendingPhonemes.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'sentence-management' ? 'active' : ''}`}
          onClick={() => setActiveTab('sentence-management')}
        >
          📝 Sentence Management
        </button>
        <button 
          className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          📊 Performance Logs
        </button>
        <button
          className={`tab-btn ${activeTab === 'user-management' ? 'active' : ''}`}
          onClick={() => setActiveTab('user-management')}
        >
          👥 User Management
        </button>
        <button
          className={`tab-btn ${activeTab === 'game-settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('game-settings')}
        >
          🎮 Game Settings
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      {/* Audio Approval Tab */}
      {activeTab === 'audio-approval' && (
        <div className="tab-content">
          <h3>Audio Approval Queue</h3>
          
          {/* Phoneme Recordings Section */}
          {pendingPhonemes.length > 0 && (
            <>
              <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>📢 Phoneme Recordings</h4>
              <div className="approval-table-container">
                <table className="approval-table">
                  <thead>
                    <tr>
                      <th>Language</th>
                      <th>Accent</th>
                      <th>Phoneme</th>
                      <th>Coach ID</th>
                      <th>Submitted</th>
                      <th>Audio</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPhonemes.map(phoneme => (
                      <tr key={phoneme.id}>
                        <td>
                          <span className="language-badge">
                            {phoneme.language === 'hindi' ? '🇮🇳 Hindi' : '🇺🇸 English'}
                          </span>
                        </td>
                        <td>{phoneme.accent}</td>
                        <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{phoneme.phoneme}</td>
                        <td className="coach-id">{phoneme.coachId?.substring(0, 15)}...</td>
                        <td>{phoneme.createdAt ? new Date(phoneme.createdAt._seconds * 1000).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          {phoneme.audioUrl && (
                            <button 
                              className="play-btn-compact"
                              onClick={() => playAudio(phoneme.audioUrl)}
                              title="Play Audio"
                            >
                              🔊
                            </button>
                          )}
                        </td>
                        <td className="action-buttons">
                          <button 
                            className="approve-btn-compact"
                            onClick={() => approvePhonemeRecording(phoneme)}
                            title="Approve"
                          >
                            ✅
                          </button>
                          <button 
                            className="reject-btn-compact"
                            onClick={() => rejectPhonemeRecording(phoneme)}
                            title="Reject"
                          >
                            ❌
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          
          {/* Sentence Recordings Section */}
          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>📝 Sentence Recordings</h4>
          
          {isLoading ? (
            <div className="loading">Loading pending recordings...</div>
          ) : pendingSentences.length > 0 ? (
            <div className="approval-table-container">
              <table className="approval-table">
                <thead>
                  <tr>
                    <th>Language</th>
                    <th>Text</th>
                    <th>Translation</th>
                    <th>Difficulty</th>
                    <th>Coach ID</th>
                    <th>Submitted</th>
                    <th>Audio</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingSentences.map(sentence => (
                    <tr key={sentence.id}>
                      <td>
                        <span className="language-badge">
                          {sentence.language === 'hi-IN' ? '🇮🇳 Hindi' : '🇺🇸 English'}
                        </span>
                      </td>
                      <td className="text-primary">
                        <div>{sentence.text}</div>
                        {sentence.transliteration && (
                          <div className="transliteration">{sentence.transliteration}</div>
                        )}
                      </td>
                      <td className="text-secondary">{sentence.translation_en}</td>
                      <td>
                        <span className={`difficulty-badge ${sentence.difficulty || 'beginner'}`}>
                          {sentence.difficulty || 'beginner'}
                        </span>
                      </td>
                      <td className="coach-id">{sentence.coach_id?.substring(0, 15)}...</td>
                      <td>{new Date(sentence.updated_at || '').toLocaleDateString()}</td>
                      <td>
                        {sentence.pending_audio_url && (
                          <button 
                            className="play-btn-compact"
                            onClick={() => playAudio(sentence.pending_audio_url!)}
                            title="Play Audio"
                          >
                            🔊
                          </button>
                        )}
                      </td>
                      <td className="action-buttons">
                        <button 
                          className="approve-btn-compact"
                          onClick={() => approveRecording(sentence)}
                          title="Approve"
                        >
                          ✅
                        </button>
                        <button 
                          className="reject-btn-compact"
                          onClick={() => {
                            setSelectedSentence(sentence);
                            setShowRejectionModal(true);
                          }}
                          title="Reject"
                        >
                          ❌
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No recordings pending approval</p>
            </div>
          )}
        </div>
      )}

      {/* Sentence Management Tab */}
      {activeTab === 'sentence-management' && (
        <div className="tab-content">
          <div className="management-header">
            <h3>Manage Sentences</h3>
            <div className="management-actions">
              <button 
                className="add-btn"
                onClick={() => setShowAddSentenceModal(true)}
              >
                ➕ Add Sentence
              </button>
              <button 
                className="import-btn"
                onClick={() => importSentencesFromJSON('hindi')}
              >
                📥 Import Hindi Sentences
              </button>
              <button 
                className="import-btn"
                onClick={() => importSentencesFromJSON('english')}
                style={{ marginLeft: '10px' }}
              >
                📥 Import English Sentences
              </button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="loading">Loading sentences...</div>
          ) : allSentences.length > 0 ? (
            <div className="sentences-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Hindi Text</th>
                    <th>English</th>
                    <th>Status</th>
                    <th>Difficulty</th>
                    <th>Audio</th>
                  </tr>
                </thead>
                <tbody>
                  {allSentences.map(sentence => (
                    <tr key={sentence.id}>
                      <td>{sentence.id}</td>
                      <td className="hindi-text">{sentence.text}</td>
                      <td>{sentence.translation_en}</td>
                      <td>{getStatusBadge(sentence.status)}</td>
                      <td>{sentence.difficulty}</td>
                      <td>
                        {sentence.reference_audio_url ? (
                          <button 
                            className="play-mini-btn"
                            onClick={() => playAudio(sentence.reference_audio_url!)}
                          >
                            🔊
                          </button>
                        ) : (
                          <span className="no-audio">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No sentences found</p>
              <button onClick={() => importSentencesFromJSON('hindi')}>Import Hindi Sentences</button>
              <button onClick={() => importSentencesFromJSON('english')} style={{ marginLeft: '10px' }}>Import English Sentences</button>
            </div>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="tab-content">
          <h3>Performance Analytics</h3>
          {isLoading ? (
            <div className="loading">Loading performance data...</div>
          ) : (
            <div className="performance-stats">
              <div className="stats-summary">
                <div className="stat-card">
                  <h4>Total Analyses</h4>
                  <p className="stat-value">{performanceLogs.length}</p>
                </div>
                <div className="stat-card">
                  <h4>Average Duration</h4>
                  <p className="stat-value">
                    {performanceLogs.length > 0 
                      ? `${(performanceLogs.reduce((sum, log) => sum + log.duration, 0) / performanceLogs.length).toFixed(2)}s`
                      : '0s'}
                  </p>
                </div>
                <div className="stat-card">
                  <h4>Error Rate</h4>
                  <p className="stat-value">
                    {performanceLogs.length > 0
                      ? `${((performanceLogs.filter(log => log.hasError).length / performanceLogs.length) * 100).toFixed(1)}%`
                      : '0%'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedSentence && (
        <div className="modal-overlay" onClick={() => setShowRejectionModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Reject Recording</h3>
            <p>Sentence: {selectedSentence.text}</p>
            <textarea
              placeholder="Please provide a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              style={{ width: '100%', marginBottom: '15px' }}
            />
            <div className="modal-actions">
              <button onClick={() => setShowRejectionModal(false)}>Cancel</button>
              <button 
                className="reject-confirm-btn"
                onClick={rejectRecording}
                disabled={!rejectionReason.trim()}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Sentence Modal */}
      {showAddSentenceModal && (
        <div className="modal-overlay" onClick={() => setShowAddSentenceModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Add New Hindi Sentence</h3>
            <div className="form-group">
              <label>Hindi Text (देवनागरी):</label>
              <input
                type="text"
                value={newSentence.text}
                onChange={(e) => setNewSentence({...newSentence, text: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Transliteration:</label>
              <input
                type="text"
                value={newSentence.transliteration}
                onChange={(e) => setNewSentence({...newSentence, transliteration: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>English Translation:</label>
              <input
                type="text"
                value={newSentence.translation_en}
                onChange={(e) => setNewSentence({...newSentence, translation_en: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Difficulty:</label>
              <select
                value={newSentence.difficulty}
                onChange={(e) => setNewSentence({...newSentence, difficulty: e.target.value})}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAddSentenceModal(false)}>Cancel</button>
              <button 
                className="add-confirm-btn"
                onClick={addNewSentence}
                disabled={!newSentence.text || !newSentence.translation_en}
              >
                Add Sentence
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Tab */}
      {activeTab === 'user-management' && (
        <UserManagement />
      )}

      {/* Game Settings Tab */}
      {activeTab === 'game-settings' && (
        <div className="game-settings-container">
          <h2>🎮 Game Settings - Beginner Level Phonemes</h2>
          <p>Select which phonemes should be included in the beginner level of Game Time!</p>

          <div style={{ marginTop: '20px' }}>
            <h3>Eligible Sounds for Beginner Level</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              Currently {beginnerPhonemes.size} sounds selected for beginner level
            </p>

            {Object.entries(HINDI_PHONEME_CATEGORIES).map(([categoryKey, category]) => (
              <div key={categoryKey} style={{ marginBottom: '30px' }}>
                <h4 style={{
                  color: category.color,
                  borderBottom: `2px solid ${category.color}`,
                  paddingBottom: '5px',
                  marginBottom: '15px'
                }}>
                  {category.name}
                </h4>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '10px'
                }}>
                  {HINDI_PHONEMES
                    .filter(phoneme => phoneme.category === categoryKey)
                    .map(phoneme => (
                      <label
                        key={phoneme.symbol}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: beginnerPhonemes.has(phoneme.symbol)
                            ? `${category.color}20`
                            : '#f5f5f5',
                          border: `1px solid ${beginnerPhonemes.has(phoneme.symbol)
                            ? category.color
                            : '#ddd'}`,
                          borderRadius: '5px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={beginnerPhonemes.has(phoneme.symbol)}
                          onChange={(e) => {
                            const newSet = new Set(beginnerPhonemes);
                            if (e.target.checked) {
                              newSet.add(phoneme.symbol);
                            } else {
                              newSet.delete(phoneme.symbol);
                            }
                            setBeginnerPhonemes(newSet);
                            // Here you would normally save to backend
                            setSuccessMessage(`Updated beginner phonemes (${newSet.size} selected)`);
                            setTimeout(() => setSuccessMessage(''), 3000);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                            {phoneme.devanagari}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            /{phoneme.symbol}/
                          </div>
                        </div>
                      </label>
                    ))}
                </div>
              </div>
            ))}

            <div style={{
              marginTop: '30px',
              padding: '15px',
              backgroundColor: '#f0f8ff',
              borderRadius: '8px',
              border: '1px solid #4CAF50'
            }}>
              <h4>Quick Actions</h4>
              <button
                onClick={() => {
                  // Select only the simplest phonemes
                  const simplePhonemes = new Set(['ə', 'aː', 'k', 'g', 'p', 'b', 'm', 'n', 'l', 's', 'h']);
                  setBeginnerPhonemes(simplePhonemes);
                  setSuccessMessage('Reset to simple phonemes only');
                  setTimeout(() => setSuccessMessage(''), 3000);
                }}
                style={{
                  padding: '8px 16px',
                  marginRight: '10px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset to Simple Sounds
              </button>

              <button
                onClick={() => {
                  // Select all vowels and basic consonants
                  const basicPhonemes = new Set(
                    HINDI_PHONEMES
                      .filter(p => p.category === 'vowel' ||
                                  p.category === 'consonant' ||
                                  p.category === 'nasal')
                      .map(p => p.symbol)
                  );
                  setBeginnerPhonemes(basicPhonemes);
                  setSuccessMessage('Selected all basic phonemes');
                  setTimeout(() => setSuccessMessage(''), 3000);
                }}
                style={{
                  padding: '8px 16px',
                  marginRight: '10px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Add Basic Consonants
              </button>

              <button
                onClick={() => {
                  setBeginnerPhonemes(new Set());
                  setSuccessMessage('Cleared all selections');
                  setTimeout(() => setSuccessMessage(''), 3000);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardEnhanced;